import React, { Component, Fragment, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import I18n from "i18n-js";

import api from "../api";

import { MAX_ACTIVE_PLANS } from "./config/constants";

import useRoutingState from "../hooks/useRoutingState";
import useMemberPlanState from "../hooks/useMemberPlanState";
import useObjectiveState from "../hooks/useObjectiveState";

import ActionButton from "../commons/ActionButton";
import ProgressBar from "../commons/ProgressBar";

import PdpSelector from "./components/PdpSelector";
import MemberPlanSelector from "./components/MemberPlanSelector";
import PlanTitle from "./components/PlanTitle";
import ObjectiveTimeline from "./components/ObjectiveTimeline";
import ObjectivesDetails from "./components/ObjectivesDetails";
import PublishButton from "./components/PublishButton";

import editIcon from "../assets/icons/edit.svg";
import approveIcon from "../assets/icons/approve.svg";
import completeIcon from "../assets/icons/complete.svg";
import confirmIcon from "../assets/icons/confirm.svg";

const Show = ({
  teams,
  team, 
  personal, 
  isTeamLeadOrAbove, 
  isTeamLead, 
  isPlanOwner, 
  activePlansCount, 
  members,
  plan
}) => {
  useRoutingState();

  const { isMemberSelected } = useMemberPlanState(members);

  const { objective, setObjective, loading } = useObjectiveState(plan?.objectives);

  const [actionLoading, setActionLoading] = useState(false);

  const progress = Math.round(plan?.progress * 100.0);

  const showCompleteButton = isPlanOwner && plan?.status === "approved";
  const showConfirmButton = isTeamLead && plan?.status === "completed";
  const showPublishButton = isPlanOwner && plan?.status === "draft";
  const showApproveButton = isTeamLead && plan?.status === "published";
  const showEditButton = isPlanOwner && (personal || ["draft", "published"].includes(plan?.status));
  const showAddTaskButton = isPlanOwner && (personal || ["draft", "published"].includes(plan?.status));
  const showTaskActions = isPlanOwner && (personal || ["draft", "published"].includes(plan?.status));
  const showTaskProgressAction = isPlanOwner && (personal || plan?.status === "approved");
  const showMyPlanSelector = isPlanOwner || isTeamLead;
  const showNoteEditor = isTeamLead;

  const canPublish = activePlansCount < MAX_ACTIVE_PLANS;

  const approvePlan = () => {
    setActionLoading(true);

    api.post(`/development_plans/${plan?.uuid}/approve`)
      .then(res => {
        console.log(res);
        setTimeout(() => {
          toast.success(<b>{I18n.t("common.approved_successfully")}</b>);

          if (res.data && res.data.redirect_to) {
            window.location.assign(res.data.redirect_to);
          }
        }, 500);
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.message) {
          toast.error(<b>{err.response.data.message}</b>);
        } else {
          toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
        }
        setActionLoading(false);
        console.error(err);
      });
  }

  const completePlan = () => {
    setActionLoading(true);

    api.post(`/development_plans/${plan?.uuid}/complete`)
      .then(res => {
        console.log(res);
        setTimeout(() => {
          toast.success(<b>{I18n.t("common.completed_successfully")}</b>);

          if (res.data && res.data.redirect_to) {
            window.location.assign(res.data.redirect_to);
          }
        }, 500);
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.message) {
          toast.error(<b>{err.response.data.message}</b>);
        } else {
          toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
        }
        setActionLoading(false);
        console.error(err);
      });
  }

  const confirmPlan = () => {
    setActionLoading(true);

    api.post(`/development_plans/${plan?.uuid}/confirm`)
      .then(res => {
        console.log(res);
        setTimeout(() => {
          toast.success(<b>{I18n.t("common.confirmed_successfully")}</b>);

          if (res.data && res.data.redirect_to) {
            window.location.assign(res.data.redirect_to);
          }
        }, 500);
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.message) {
          toast.error(<b>{err.response.data.message}</b>);
        } else {
          toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
        }
        setActionLoading(false);
        console.error(err);
      });
  }

  const getPdpLink = (postfix = "", params = []) => {
    const base = team ? `/teams/${team?.uuid}/pdp/` : "/pdp/";
    return base + postfix + `?show_back=true&locale=${I18n.locale}`;
  }

  const getRelativeLink = (path) => getPdpLink(`${plan?.uuid}/${path}`);

  const renderPageHeader = () => (
    <div className="pdp-row page-header">
      <div className="selectors">
        <PdpSelector
          teams={teams}
          selectedTeam={team}
          personal={personal}
          isTeamLeadOrAbove={isTeamLeadOrAbove}
        />
        {members?.length ? (
          <MemberPlanSelector
            team={team}
            members={members}
            showMyPlan={showMyPlanSelector}
            isMemberSelected={isMemberSelected}
          />
        ) : null}
        <PlanTitle title={plan.title} />
      </div>
      <div className="action-buttons">
        <div className="plan-progress">
          <ProgressBar value={progress} />
          <span className="indicator">
            {I18n.t("pdp.progress.status", { progress: progress })}
          </span>
        </div>
        {showApproveButton
          ? <ActionButton loading={actionLoading} title={I18n.t("pdp.actions.plan.approve")} icon={approveIcon} onClick={approvePlan} />
          : showCompleteButton
            ? <ActionButton loading={actionLoading} title={I18n.t("pdp.actions.plan.complete")} icon={completeIcon} onClick={completePlan} />
            : showConfirmButton
              ? <ActionButton loading={actionLoading} title={I18n.t("pdp.actions.plan.confirm")} icon={confirmIcon} onClick={confirmPlan} />
              : showPublishButton
                ? <PublishButton plan={plan} canPublish={canPublish} />
                : null
        }
        {showEditButton ? (
          <ActionButton
            title={I18n.t("pdp.actions.plan.edit")}
            icon={editIcon}
            href={getRelativeLink("edit")}
          />
        ) : null}
        <ActionButton
          title={I18n.t("pdp.actions.plan.summary")}
          href={getRelativeLink("summary")}
        />
        <ActionButton
          title={I18n.t("pdp.actions.plan.all")}
          href={getPdpLink("all")}
        />
      </div>
    </div>
  );

  const renderPageContent = () => (
    <div className="page-content">
      <ObjectiveTimeline objective={objective} />
      <div className="objectives-panel">
        <span className="section-title">{I18n.t("pdp.objectives.title")}</span>
        <ObjectivesDetails
          team={team}
          plan={plan}
          objectives={plan?.objectives}
          selectedObjective={objective}
          isPlanOwner={isPlanOwner}
          showAddTaskButton={showAddTaskButton}
          showTaskActions={showTaskActions}
          showTaskProgressAction={showTaskProgressAction}
          showNoteEditor={showNoteEditor}
          onObjectiveSelect={setObjective}
        />
      </div>
    </div>
  );

  return (
    <Fragment>
      <div className="pdp">
        <div className="container-fluid">
          {renderPageHeader()}
          <div className="pdp-row">
            {renderPageContent()}
          </div>
        </div>
      </div>
      <Toaster
        position="top-right"
        reverseOrder
      />
    </Fragment>
  );
}

// NOTE: needed now to current version of ReactOnRails to work with hooks
export default class ShowComponent extends Component {
  render() {
    return <Show {...this.props} />;
  }
}